import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import type { MissionListFilters } from "@/types/missions"

/**
 * OKS-31 — Active-filter chips above the mission list table.
 *
 * Renders one removable `Badge` per non-default filter; clicking the X
 * resets that one filter (returns it to its default — `active` for
 * status, `all` for mission_type / deadline_window, `null` for q).
 *
 * Mirrors the OKS-25 `crew-list-active-filter-chips` UX: chips fade out
 * on remove (via shadcn Badge default transitions); group sits between
 * the filter rail and the table.
 */
interface MissionListActiveFilterChipsProps {
  filters: MissionListFilters
  onResetStatus: () => void
  onResetMissionType: () => void
  onResetDeadlineWindow: () => void
  onResetSearch: () => void
}

export function MissionListActiveFilterChips({
  filters,
  onResetStatus,
  onResetMissionType,
  onResetDeadlineWindow,
  onResetSearch,
}: MissionListActiveFilterChipsProps) {
  const { t } = useTranslation()

  const chips: { key: string; label: string; onRemove: () => void }[] = []

  if (filters.status !== "active") {
    chips.push({
      key: "status",
      label: t("missions.index.filters.chip_status", {
        value: t(`missions.index.filters.status_options.${filters.status}`),
      }),
      onRemove: onResetStatus,
    })
  }

  if (filters.mission_type !== "all") {
    chips.push({
      key: "mission_type",
      label: t("missions.index.filters.chip_mission_type", {
        value: t(
          `missions.index.filters.mission_type_options.${filters.mission_type}`,
        ),
      }),
      onRemove: onResetMissionType,
    })
  }

  if (filters.deadline_window !== "all") {
    chips.push({
      key: "deadline_window",
      label: t("missions.index.filters.chip_deadline_window", {
        value: t(
          `missions.index.filters.deadline_window_options.${filters.deadline_window}`,
        ),
      }),
      onRemove: onResetDeadlineWindow,
    })
  }

  if (filters.q && filters.q.trim() !== "") {
    chips.push({
      key: "q",
      label: t("missions.index.filters.chip_search", { value: filters.q }),
      onRemove: onResetSearch,
    })
  }

  if (chips.length === 0) return null

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="mission-list-active-filter-chips"
      role="group"
      aria-label={t("missions.index.filters.chip_group_aria")}
    >
      {chips.map((chip) => (
        <Badge
          key={chip.key}
          variant="secondary"
          className="gap-1 pr-1"
          data-testid={`mission-list-active-filter-chip-${chip.key}`}
        >
          <span>{chip.label}</span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={t("missions.index.filters.chip_remove_aria", {
              filter: chip.label,
            })}
            className="hover:bg-background/50 -my-0.5 rounded-sm p-0.5"
            data-testid={`mission-list-active-filter-chip-${chip.key}-remove`}
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </Badge>
      ))}
    </div>
  )
}
